import { Fragment, type ReactNode } from "react";
import { HatTextTooltip } from "@/components/tooltip";
import { EmptyState, noticeChipClassName } from "@/shared/ui/emptyState/public";
import { DIAGNOSTICS_GROUP_TERMS } from "../diagnosticsTerms";
import { GROUP_ORDER, channelTitle, type ChannelStructureIndex } from "../parquetChannels";
import { actionSourceDotColor, actionSourceTerm } from "../segments/actionSourceTerms";
import {
  motionBarPosition,
  motionSummaryCards,
  type MotionSummaryCard,
  type MotionSummaryRow,
} from "./motionSummaryBars";
import {
  motionMetricTerm,
  motionStatLabel,
  motionStatTip,
  motionUnavailableText,
} from "./motionTerms";
import type { EpisodeTimeline, TimelineEvent, TimelineMetric } from "./timelineArtifact";
import { timelineSegmentGroups, type TimelineSourceGroup } from "./timelineSegmentGroups";
import band from "../EpisodeParquetDeviationVisual.module.css";
import tables from "../diagnosticsTables.module.css";
import styles from "./MotionSummary.module.css";

// 눈금 설명. 막대 길이의 기준을 한 번만 설명한다. 카드마다 눈금을 적으면 같은 두 수가 화면에
// 수십 번 나온다. 적지 않으면 사용자는 오른쪽 숫자를 막대 끝 값으로 이해한다.
const SCALE_NOTE =
  "막대 길이는 같은 지표를 낸 채널이 함께 쓰는 축 위의 자리입니다. 오른쪽 숫자는 그 채널의 최댓값입니다.";
const P95_NOTE =
  "p95는 분석용 표본의 95백분위수입니다. 시간 비중의 95%를 뜻하지 않으며, 상위 5%의 평균이 아닙니다. 평균·RMS는 시간 가중 값입니다.";

/** 통계 한 수의 표기. 유효 표본이 없으면 0 으로 표시하지 않는다. 값이 없는 것은 멈춰 있었다는 뜻이 아니다. */
function value(input: number | null): string {
  return input === null ? "—" : Number(input.toPrecision(5)).toString();
}

/** 막대가 표시하지 않는 수를 포함해 모든 수를 적는다. 막대는 크기를, 이 글은 값을 표시한다. */
function statsText(metric: TimelineMetric): string {
  return [
    `${motionStatLabel("mean")} ${value(metric.mean)}`,
    `${motionStatLabel("p95")} ${value(metric.p95)}`,
    `${motionStatLabel("rms")} ${value(metric.rms)}`,
    `최소 ${value(metric.min?.value ?? null)}`,
    `${motionStatLabel("max")} ${value(metric.max?.value ?? null)}${
      metric.max === null ? "" : ` (${metric.max.timestamp.toFixed(2)}초)`
    }`,
    `유효 표본 ${metric.count ?? "—"}`,
  ].join(" · ");
}

/**
 * 저장된 요약 통계. 채널 밴드와 같은 틀의 카드 격자다.
 *
 * 시간별 곡선의 표본으로 요약값을 대신하지 않는다. 요약값은 상류가 원본 전체 범위로 따로
 * 계산한 값이다. 카드는 **모든 그룹**을 표시한다. 채널 밴드가 Action·Observation 을 함께
 * 표시하는 것과 같다.
 *
 * 적재와 미준비 문구는 호출부가 소유한다. 이 표면은 파싱된 타임라인만 받는다. 적재 훅을 접고
 * 펴는 자리에 두면 접을 때 훅 state 가 사라지고, 펼 때 본문을 다시 받는다(창원 ep26 실측 145 KB).
 */
export function MotionSummary({
  timeline,
  channels,
}: {
  timeline: EpisodeTimeline;
  channels: ChannelStructureIndex;
}) {
  return (
    <div className={band.wrap}>
      <p className={styles.caption}>{P95_NOTE}</p>
      <p className={styles.caption}>{SCALE_NOTE}</p>
      <Legend />
      {timeline.episode === null ? (
        <EmptyState size="sm">에피소드 전체 통계가 없습니다.</EmptyState>
      ) : (
        <SummaryCards event={timeline.episode} channels={channels} label="에피소드 전체 통계" />
      )}
      <SegmentGroups timeline={timeline} channels={channels} />
    </div>
  );
}

/**
 * 구간별 통계. 출처(action_source)별로 묶고 접는다.
 *
 * **묶기만 하고 집계하지 않는다.** 길이 합을 제외하면 구간을 합쳐 다시 계산할 수 있는 통계가
 * 없다(p95 는 표본 분위수다). 표시할 수치가 없는 구간은 제외한다. 창원 실측에서 구간의 절반
 * 이상이 표본 0개인 경계 조각이다.
 */
function SegmentGroups({
  timeline,
  channels,
}: {
  timeline: EpisodeTimeline;
  channels: ChannelStructureIndex;
}): ReactNode {
  const grouping = timelineSegmentGroups(timeline);
  if (grouping.shown === 0) {
    return <EmptyState size="sm">수치를 낸 구간이 없습니다.</EmptyState>;
  }
  const dropped = grouping.total - grouping.shown;
  return (
    <section aria-label="구간별 통계" className={styles.segments}>
      <div className={band.head}>
        <span className={band.title}>구간별 통계</span>
        <span className={band.meta}>
          {grouping.shown}개 구간
          {dropped > 0 ? ` · 수치가 없는 구간 ${dropped}개는 뺐습니다` : ""}
        </span>
      </div>
      {grouping.groups.map((group) => (
        <SourceGroup key={group.state ?? ""} group={group} channels={channels} />
      ))}
    </section>
  );
}

function SourceGroup({
  group,
  channels,
}: {
  group: TimelineSourceGroup;
  channels: ChannelStructureIndex;
}): ReactNode {
  // 출처 이름·색은 구간 보기의 띠·칩과 같은 용어를 쓴다. 두 표면이 같은 구간을 다르게 부르지 않는다.
  const term = group.category === null ? null : actionSourceTerm(group.category);
  const label =
    term === null
      ? "출처 기록 없음"
      : group.category === "unknown"
        ? `정의 밖 값 ${group.state}`
        : term.label;
  return (
    <details className={styles.sourceGroup}>
      <summary>
        {term === null ? null : (
          <span
            className={tables.dot}
            aria-hidden="true"
            style={{ background: actionSourceDotColor(term.dataViz) }}
          />
        )}
        {label} {group.segments.length}개 · 합계 {group.totalSeconds.toFixed(2)}초
      </summary>
      {group.segments.map((segment) => (
        <details className={styles.segment} key={`${segment.index}:${segment.startSeconds}`}>
          <summary>
            구간 {segment.index} · {segment.startSeconds.toFixed(2)}–{segment.endSeconds.toFixed(2)}
            초
          </summary>
          <SegmentTable event={segment.event} channels={channels} />
        </details>
      ))}
    </details>
  );
}

// 구간 수치는 표로 표시한다. 구간이 100개를 넘을 수 있으므로 구간마다 카드 격자를 만들지 않는다.
const SEGMENT_STATS = ["mean", "p95", "rms"] as const;

function SegmentTable({
  event,
  channels,
}: {
  event: TimelineEvent;
  channels: ChannelStructureIndex;
}): ReactNode {
  const cards = motionSummaryCards(event, channels);
  const ordered = GROUP_ORDER.flatMap((group) => cards.filter((card) => card.group === group));
  return (
    <div className={tables.scroll}>
      <table className={tables.table}>
        <thead>
          <tr>
            <th scope="col">채널</th>
            <th scope="col">지표</th>
            {SEGMENT_STATS.map((stat) => (
              <th key={stat} scope="col">
                <HatTextTooltip text={motionStatTip(stat)} placement="bottom" align="start">
                  <span className={tables.tipLabel}>{motionStatLabel(stat)}</span>
                </HatTextTooltip>
              </th>
            ))}
            <th scope="col">최소</th>
            <th scope="col">
              <HatTextTooltip text={motionStatTip("max")} placement="bottom" align="start">
                <span className={tables.tipLabel}>{motionStatLabel("max")}</span>
              </HatTextTooltip>
            </th>
            <th scope="col">유효 표본</th>
          </tr>
        </thead>
        <tbody>
          {ordered.map((card, index) => (
            <Fragment key={card.channel}>
              {/* 그룹이 바뀌는 자리에 머리 행을 둔다. 이 머리 행이 이름이 같은 Observation·Action
                  채널을 구분한다. */}
              {index === 0 || ordered[index - 1]!.group !== card.group ? (
                <tr className={tables.groupRow}>
                  <th scope="rowgroup" colSpan={SEGMENT_STATS.length + 5}>
                    {DIAGNOSTICS_GROUP_TERMS[card.group].label}
                  </th>
                </tr>
              ) : null}
              {card.rows.length === 0 ? (
                <tr>
                  <th scope="row">{channelTitle(card.channel, channels.get(card.channel))}</th>
                  <td className={tables.reason} colSpan={SEGMENT_STATS.length + 4}>
                    {card.unavailable === null
                      ? "이 채널의 지표가 없습니다."
                      : motionUnavailableText(card.unavailable)}
                  </td>
                </tr>
              ) : (
                card.rows.map((row) => (
                  <tr key={row.key}>
                    <th scope="row">{channelTitle(card.channel, channels.get(card.channel))}</th>
                    <td className={tables.labelCell}>{motionMetricTerm(row.key).label}</td>
                    <td>{value(row.metric.mean)}</td>
                    <td>{value(row.metric.p95)}</td>
                    <td>{value(row.metric.rms)}</td>
                    <td>{value(row.metric.min?.value ?? null)}</td>
                    <td>{value(row.metric.max?.value ?? null)}</td>
                    <td>{row.metric.count ?? "—"}</td>
                  </tr>
                ))
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Legend(): ReactNode {
  return (
    <div className={styles.legend}>
      <span className={styles.legendItem}>
        <span className={styles.legendWhisker} aria-hidden="true" />
        최소–최대
      </span>
      <HatTextTooltip text={motionStatTip("p95")} placement="bottom" align="start">
        <span className={styles.legendItem}>
          <span className={styles.legendBox} aria-hidden="true" />
          최소–{motionStatLabel("p95")}
        </span>
      </HatTextTooltip>
      <HatTextTooltip text={motionStatTip("mean")} placement="bottom" align="start">
        <span className={styles.legendItem}>
          <span className={styles.legendMeanBox} aria-hidden="true" />
          {motionStatLabel("mean")}
        </span>
      </HatTextTooltip>
    </div>
  );
}

/** 한 구간(또는 에피소드 전체)의 채널 카드 격자. 그룹 머리글은 격자 한 줄을 차지한다. */
export function SummaryCards({
  event,
  channels,
  label,
}: {
  event: TimelineEvent;
  channels: ChannelStructureIndex;
  label: string;
}): ReactNode {
  const cards = motionSummaryCards(event, channels);
  if (cards.length === 0) {
    return <EmptyState size="sm">{label}에 표시할 채널이 없습니다.</EmptyState>;
  }
  return (
    <section aria-label={label}>
      <div className={band.head}>
        <span className={band.title}>{label}</span>
        <span className={band.meta}>{cards.length}개 채널</span>
      </div>
      <div className={band.columns}>
        {GROUP_ORDER.map((group) => {
          const groupCards = cards.filter((card) => card.group === group);
          if (groupCards.length === 0) return null;
          const term = DIAGNOSTICS_GROUP_TERMS[group];
          return (
            <Group key={group} label={term.label} tip={term.tip} count={groupCards.length}>
              {groupCards.map((card) => (
                <Card key={card.channel} card={card} channels={channels} />
              ))}
            </Group>
          );
        })}
      </div>
    </section>
  );
}

function Group({
  label,
  tip,
  count,
  children,
}: {
  label: string;
  tip: string;
  count: number;
  children: ReactNode;
}): ReactNode {
  return (
    <>
      <div className={band.groupHead}>
        <HatTextTooltip text={tip} placement="bottom" align="start">
          <span className={band.groupTitle}>{label}</span>
        </HatTextTooltip>
        <span className={band.groupCount}>{count}개 채널</span>
      </div>
      {children}
    </>
  );
}

function Card({
  card,
  channels,
}: {
  card: MotionSummaryCard;
  channels: ChannelStructureIndex;
}): ReactNode {
  const headline = card.headline;
  return (
    <div className={band.column}>
      <div className={band.columnHead}>
        <span className={band.columnTitle}>
          {channelTitle(card.channel, channels.get(card.channel))}
        </span>
        {headline === null ? null : (
          // 어느 지표의 p95 인지 함께 적는다. pose 채널은 지표가 셋이라 수만으로는 구분할 수 없다.
          <span className={band.columnMax}>
            {motionMetricTerm(headline.key).label} {motionStatLabel("p95")}{" "}
            {value(headline.metric.p95)}
          </span>
        )}
      </div>
      {card.unavailable !== null ? (
        <p className={styles.cardNotice}>
          <span className={noticeChipClassName}>{motionUnavailableText(card.unavailable)}</span>
        </p>
      ) : card.rows.length === 0 ? (
        <p className={styles.cardNotice}>
          <span className={noticeChipClassName}>이 채널의 지표가 없습니다.</span>
        </p>
      ) : (
        <div className={styles.rows}>
          {card.rows.map((row) => (
            <Row key={row.key} row={row} />
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ row }: { row: MotionSummaryRow }): ReactNode {
  const term = motionMetricTerm(row.key);
  const max = row.metric.max?.value ?? null;
  return (
    <div className={styles.row}>
      <HatTextTooltip text={`${term.label} — ${term.tip}`} placement="bottom" align="start">
        <span className={styles.rowLabel}>{term.label}</span>
      </HatTextTooltip>
      <HatTextTooltip text={`${term.label} · ${statsText(row.metric)}`} placement="bottom">
        <Bar row={row} />
      </HatTextTooltip>
      <span className={styles.rowValue}>{value(max)}</span>
    </div>
  );
}

function Bar({ row }: { row: MotionSummaryRow }): ReactNode {
  const scale = row.scale;
  if (scale === null) {
    return <span className={styles.unavailable}>값 없음</span>;
  }
  const at = (input: number | null | undefined) =>
    typeof input === "number" && Number.isFinite(input) ? motionBarPosition(input, scale) : null;
  const low = at(row.metric.min?.value) ?? 0;
  const high = at(row.metric.max?.value) ?? low;
  const p95 = at(row.metric.p95);
  const mean = at(row.metric.mean);
  const percent = (fraction: number) => `${fraction * 100}%`;
  return (
    <span className={styles.plot} aria-hidden="true">
      <span
        className={styles.whisker}
        style={{ left: percent(low), width: percent(Math.max(0, high - low)) }}
      />
      {p95 === null ? null : (
        <span
          className={styles.box}
          style={{ left: percent(low), width: percent(Math.max(0, p95 - low)) }}
        />
      )}
      {mean === null ? null : <span className={styles.meanMark} style={{ left: percent(mean) }} />}
    </span>
  );
}
